import { useState, useEffect, useContext } from 'react';
import { AppContext } from '@/App';
import axios from 'axios';
import { Scale, Search, Filter, Plus, FileText, TrendingUp, TrendingDown } from 'lucide-react';

function Weighment() {
  const { API } = useContext(AppContext);
  const [weighmentData, setWeighmentData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');

  useEffect(() => {
    fetchWeighmentData();
  }, [filterType]);

  const fetchWeighmentData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/stores/weighment`, {
        params: { type: filterType },
        
      });
      setWeighmentData(response.data);
    } catch (error) {
      console.error('Error fetching weighment:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredData = weighmentData.filter(item =>
    item.vehicleNo.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.material.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.id.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const stats = {
    totalInward: weighmentData.filter(w => w.type === 'Inward').reduce((sum, w) => sum + w.netWeight, 0),
    totalOutward: weighmentData.filter(w => w.type === 'Outward').reduce((sum, w) => sum + w.netWeight, 0),
    todayEntries: weighmentData.filter(w => w.date === '2025-02-01').length,
    totalEntries: weighmentData.length,
  };

  if (loading) {
    return <div className="text-center py-12">Loading weighment data...</div>;
  }

  return (
    <div className="space-y-6" data-testid="stores-weighment-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Stores Weighment Details</h1>
          <p className="text-gray-500 mt-1">Track all inward and outward material weighments</p>
        </div>
        <button className="bg-gradient-to-r from-[#1a237e] to-[#283593] text-white px-4 py-2.5 rounded-xl text-sm font-medium flex items-center space-x-2 shadow-md">
          <Plus size={18} />
          <span>New Weighment</span>
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-5 border border-green-200">
          <div className="flex items-center space-x-2">
            <TrendingDown className="text-green-500" size={18} />
            <p className="text-sm text-green-600 font-medium">Total Inward</p>
          </div>
          <p className="text-2xl font-bold text-green-700">{(stats.totalInward / 1000).toFixed(1)} MT</p>
        </div>
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-5 border border-blue-200">
          <div className="flex items-center space-x-2">
            <TrendingUp className="text-blue-500" size={18} />
            <p className="text-sm text-blue-600 font-medium">Total Outward</p>
          </div>
          <p className="text-2xl font-bold text-blue-700">{(stats.totalOutward / 1000).toFixed(1)} MT</p>
        </div>
        <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
          <p className="text-sm text-gray-500">Today's Entries</p>
          <p className="text-2xl font-bold text-gray-900">{stats.todayEntries}</p>
        </div>
        <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
          <p className="text-sm text-gray-500">Total Entries</p>
          <p className="text-2xl font-bold text-gray-900">{stats.totalEntries}</p>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex items-center space-x-4">
        <div className="flex items-center space-x-2 bg-white rounded-xl p-1.5 border border-gray-200">
          {['all', 'Inward', 'Outward'].map((type) => (
            <button
              key={type}
              onClick={() => setFilterType(type)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                filterType === type
                  ? 'bg-gradient-to-r from-[#1a237e] to-[#283593] text-white shadow-md'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {type === 'all' ? 'All' : type}
            </button>
          ))}
        </div>
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="Search by vehicle, material, or ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#1a237e]/20 focus:border-[#1a237e] outline-none"
          />
        </div>
      </div>

      {/* Weighment Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-gray-50 to-slate-50">
              <tr>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Slip No</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Date/Time</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Vehicle</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Material</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Party</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Gross</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Tare</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Net</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Type</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredData.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-4 font-mono text-sm text-[#1a237e] font-medium">{item.id}</td>
                  <td className="px-4 py-4 text-sm text-gray-600">{item.date} <span className="text-gray-400">{item.time}</span></td>
                  <td className="px-4 py-4 font-mono text-sm text-gray-900">{item.vehicleNo}</td>
                  <td className="px-4 py-4 text-sm font-medium text-gray-900">{item.material}</td>
                  <td className="px-4 py-4 text-sm text-gray-600">{item.supplier || item.customer}</td>
                  <td className="px-4 py-4 text-sm text-gray-600">{item.grossWeight.toLocaleString()} kg</td>
                  <td className="px-4 py-4 text-sm text-gray-600">{item.tareWeight.toLocaleString()} kg</td>
                  <td className="px-4 py-4 text-sm font-bold text-gray-900">{item.netWeight.toLocaleString()} kg</td>
                  <td className="px-4 py-4">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                      item.type === 'Inward' 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-blue-100 text-blue-700'
                    }`}>
                      {item.type}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default Weighment;
